import {
  Area,
  AreaChart,
  CartesianGrid,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  type TooltipProps,
} from 'recharts';
import { useState } from 'react';
import type { ProjectionYear } from '../engine/projection';
import { fmtCompact, fmtMoney } from '../utils/format';

interface Props {
  years: ProjectionYear[];
  startingCalendarYear: number;
  currentAge: number;
  retirementAge: number;
}

const STACKS = [
  { key: 'taxable', label: 'Taxable', color: '#f59e0b' },
  { key: 'retirement', label: '401(k)', color: '#8b5cf6' },
  { key: 'hysa', label: 'HYSA', color: '#0891b2' },
  { key: 'homeEquity', label: 'Home equity', color: '#14b8a6' },
] as const;

export function BucketChart({ years, startingCalendarYear, currentAge, retirementAge }: Props) {
  const [visible, setVisible] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(STACKS.map(s => [s.key, true])),
  );

  const data = years.map(y => ({
    year: y.calendarYear,
    age: y.age,
    hysa: Math.round(y.hysa.end),
    retirement: Math.round(y.retirement.end),
    taxable: Math.round(y.taxable.end),
    homeEquity: Math.round(Math.max(0, y.homeValue - y.mortgageBalance)),
  }));

  const retirementYear = startingCalendarYear + (retirementAge - currentAge);

  return (
    <div className="space-y-3">
      <div className="h-72 w-full">
        <ResponsiveContainer>
          <AreaChart data={data} margin={{ top: 10, right: 12, left: 0, bottom: 0 }}>
            <CartesianGrid stroke="#eef0f4" strokeDasharray="3 6" vertical={false} />
            <XAxis
              dataKey="year"
              type="number"
              domain={['dataMin', 'dataMax']}
              tick={{ fill: '#828a9c', fontSize: 11 }}
              tickLine={false}
              axisLine={{ stroke: '#dde1e9' }}
            />
            <YAxis
              tickFormatter={v => fmtCompact(v)}
              tick={{ fill: '#828a9c', fontSize: 11 }}
              tickLine={false}
              axisLine={{ stroke: '#dde1e9' }}
              width={52}
            />
            <Tooltip
              cursor={{ stroke: '#525b6f', strokeDasharray: '2 4' }}
              content={
                <StackTooltip
                  visible={visible}
                  startingCalendarYear={startingCalendarYear}
                  currentAge={currentAge}
                />
              }
            />
            <ReferenceLine
              x={retirementYear}
              stroke="#525b6f"
              strokeDasharray="3 3"
              label={{ value: `Retire @ ${retirementAge}`, position: 'insideTop', fill: '#525b6f', fontSize: 11 }}
            />
            {STACKS.filter(s => visible[s.key]).map(s => (
              <Area
                key={s.key}
                type="monotone"
                dataKey={s.key}
                stackId="stack"
                stroke={s.color}
                fill={s.color}
                fillOpacity={0.5}
                strokeWidth={1.5}
                name={s.label}
                isAnimationActive={false}
                activeDot={{ r: 4, strokeWidth: 2, fill: '#fff', stroke: s.color }}
              />
            ))}
          </AreaChart>
        </ResponsiveContainer>
      </div>

      <div className="flex flex-wrap gap-1.5">
        {STACKS.map(s => {
          const on = visible[s.key];
          return (
            <button
              key={s.key}
              type="button"
              onClick={() => setVisible(v => ({ ...v, [s.key]: !v[s.key] }))}
              className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-medium transition ${
                on
                  ? 'border-ink-200 bg-white text-ink-900 dark:border-ink-700 dark:bg-ink-800 dark:text-ink-50'
                  : 'border-ink-100 bg-ink-50 text-ink-400 dark:border-ink-800 dark:bg-ink-900 dark:text-ink-300'
              }`}
              aria-pressed={on}
            >
              <span className="inline-block h-2 w-2 rounded-full" style={{ backgroundColor: on ? s.color : '#cbd5e1' }} />
              {s.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function StackTooltip({
  active,
  payload,
  label,
  visible,
  startingCalendarYear,
  currentAge,
}: TooltipProps<number, string> & {
  visible: Record<string, boolean>;
  startingCalendarYear: number;
  currentAge: number;
}) {
  if (!active || !payload || payload.length === 0) return null;
  const year = Number(label);
  const age = year - startingCalendarYear + currentAge;
  const total = STACKS.filter(s => visible[s.key]).reduce((sum, s) => {
    const v = payload.find(p => p.dataKey === s.key)?.value;
    return sum + (typeof v === 'number' ? v : 0);
  }, 0);
  return (
    <div className="rounded-xl border border-ink-200 bg-white/98 p-3 shadow-card backdrop-blur dark:bg-ink-800/98 dark:border-ink-700">
      <div className="mb-2 flex items-baseline justify-between border-b border-ink-100 pb-1.5 dark:border-ink-700">
        <span className="font-mono text-xs font-semibold tabular-nums">{year}</span>
        <span className="text-[11px] text-ink-500 dark:text-ink-300">age {age}</span>
      </div>
      <table className="min-w-[180px] text-xs">
        <tbody>
          {STACKS.filter(s => visible[s.key]).map(s => {
            const entry = payload.find(p => p.dataKey === s.key);
            if (!entry) return null;
            return (
              <tr key={s.key}>
                <td className="pr-3 py-0.5">
                  <span className="mr-1.5 inline-block h-2 w-2 rounded-full align-middle" style={{ backgroundColor: s.color }} />
                  <span className="text-ink-700 dark:text-ink-100">{s.label}</span>
                </td>
                <td className="py-0.5 text-right font-mono font-semibold tabular-nums">{fmtMoney(entry.value as number)}</td>
              </tr>
            );
          })}
          <tr className="border-t border-ink-100 dark:border-ink-700">
            <td className="pr-3 pt-1 font-semibold">Total</td>
            <td className="pt-1 text-right font-mono font-bold tabular-nums">{fmtMoney(total)}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}
